import { ArrowBackIos } from '@finn/design-system/icons/arrow-back-ios';
import { ArrowForwardIos } from '@finn/design-system/icons/arrow-forward-ios';
import { interactionTrackingEvent, TrackingEventName } from '@finn/ua-tracking';
import { Image } from '@finn/ui-components';
import { cn } from '@finn/ui-utils';
import { useRef, useState } from 'react';
import Slider from 'react-slick';

import { PDPTracking } from '../../tracking/PDPTracking';

type Props = {
  imageUrls: (string | undefined)[];
  imageSrcSets: (string | undefined)[];
  showDisclaimer?: boolean;
  disclaimerText: string;
  initialSlide: number;
  altText?: string;
};

export const ImageGalleryMobileV2 = ({
  imageUrls,
  imageSrcSets,
  showDisclaimer,
  disclaimerText,
  initialSlide,
  altText = 'car',
}: Props) => {
  const sliderRef = useRef(null);
  const [imageIndex, setImageIndex] = useState(0);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide,
    afterChange: (index: number) => {
      setImageIndex(index);
    },
    onSwipe: (direction: string) => {
      if (direction === 'right') {
        PDPTracking.previousSwipe();
      } else {
        PDPTracking.nextSwipe();
      }
    },
    dotsClass:
      'slick-dots bottom-4 !flex justify-center gap-1 [&_button]:!hidden [&_li]:opacity-50 [&_li.slick-active]:opacity-100 [&_li]:m-0 [&_li]:border-snow [&_li]:border [&_li]:border-solid [&_li]:w-[7px] [&_li]:h-[7px] [&_li]:bg-black [&_li]:rounded-full',
  };

  const handleGallerySlide = (nextIndex: number, control: string) => {
    if (sliderRef.current) {
      if (control === 'arrow-right') {
        sliderRef.current.slickNext();
      } else {
        sliderRef.current.slickPrev();
      }
      const idx =
        nextIndex < 0 ? imageUrls.length - 1 : nextIndex % imageUrls.length;
      interactionTrackingEvent(TrackingEventName.GALLERY_CONTROL_ACTION, {
        control,
        prevIndex: imageIndex + 1,
        newIndex: idx + 1,
      });
    }
  };

  return (
    <div className="relative -mx-8 ml-0 flex h-auto w-full overflow-hidden bg-white">
      <div
        className={cn(
          'bg-snow mb-0 w-full overflow-hidden rounded sm:mt-0 [&_.slick-list]:w-full [&_.slick-track]:flex [&_.slick-track]:items-center',
          '[&_.slick-slide>div]:bg-snow [&_.slick-slide>div]:h-full'
        )}
      >
        <Slider ref={sliderRef} {...sliderSettings}>
          {imageUrls.map((picture, i) => (
            <Image
              key={i}
              loading={i === 0 ? 'eager' : 'lazy'}
              draggable={false}
              className={cn(
                'h-full max-h-[400px] w-full max-w-full select-none mix-blend-multiply sm:max-h-[270px]'
              )}
              src={picture}
              srcSet={imageSrcSets[i]}
              alt={altText}
            />
          ))}
        </Slider>
      </div>
      <div className="body-12-medium z-overlay absolute left-1 top-1/2 flex -translate-y-1/2 gap-1 rounded-md px-2 py-3">
        <div
          className="border-pearl hover:bg-snow cursor-pointer rounded-full border-[1px] bg-white px-1.5 py-1"
          onClick={() => handleGallerySlide(imageIndex - 1, 'arrow-left')}
        >
          <ArrowBackIos width={13} height={13} />
        </div>
      </div>
      <div className="body-12-medium z-overlay absolute right-1 top-1/2 flex -translate-y-1/2 gap-1 rounded-md px-2 py-3">
        <div
          className="border-pearl hover:bg-snow cursor-pointer rounded-full border-[1px] bg-white px-1.5 py-1"
          onClick={() => handleGallerySlide(imageIndex + 1, 'arrow-right')}
        >
          <ArrowForwardIos width={13} height={13} />
        </div>
      </div>
      {showDisclaimer && (
        <div className="body-12-regular absolute bottom-4 right-4 mb-1 max-w-[80%] text-right">
          {disclaimerText}
        </div>
      )}
    </div>
  );
};
